import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useStableAccessToken } from '@app/GranularDeploy/hooks/useStableAccessToken';
import {
  Alert,
  Bullseye,
  Button,
  EmptyState,
  EmptyStateBody,
  Label,
  Spinner,
  Switch,
  TextInput,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import EditableImageCell from '@app/GranularDeploy/components/EditableImageCell';
import {
  OverlayCandidate,
  BaselineCandidate,
  PreviewService,
  InfraService,
  MirrordOverrides,
  DbBranchUiType,
  DbBranchEntry,
  DbBranchParamValue,
  apiMigrationsCheck,
  ServiceDependency,
} from '../helpers/apiHelpers';
import { C, changedBadge, countOverrides, ServiceOverridesPanel, InfraSection } from './ServiceOverridesPanel';
import { inferVersionFromImage, extractFullImageTag, matchEnvVarsToDbFields, resolveDbBranchName, DB_DEFAULT_VERSIONS } from '../../../../shared/previewEnv/matchEnvVarsToDbFields';
import { groupBySharedDep } from '../../../../shared/previewEnv/groupSharedDeps';

const ARTIFACTORY_REGISTRY = 'docker.artifactory.intouchhealth.io/tdh';
const stripAnnotation = (s: string) => s.replace(/\s*\(.+?\)\s*$/, '');
const bestTag = (c: OverlayCandidate) => (c.images?.deploy_tags ?? [])[0] ?? '';
const hasAnyImage = (c: OverlayCandidate) => (c.images?.found_tags?.length ?? 0) > 0;
// Migrations only apply to SQL entries — targeting by type instead of array position 0 avoids
// misattaching them to a redis/mongo entry when one was toggled on before the SQL entry.
const findSqlEntryIndex = (branches: DbBranchEntry[]) => branches.findIndex((b) => b.type === 'mysql' || b.type === 'postgres');
const KAFKA_DEP_REGEX = /kafka|karafka/i;
// Caps concurrent /api/preview/migrations-check calls (one per candidate with DB branching
// enabled) so a session with many such services can't burst into an unbounded fanout against
// the backend/GitHub compare API.
const MIGRATIONS_CHECK_CONCURRENCY = 4;
async function runWithConcurrencyLimit(tasks: (() => Promise<void>)[], limit: number): Promise<void> {
  let next = 0;
  async function worker() {
    while (next < tasks.length) {
      const task = tasks[next++];
      await task();
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
}

const FEATURE_FLAGS: { key: string; label: string; description: string; depRegex: RegExp }[] = [
  {
    key: 'redis',
    label: 'Redis',
    description: 'Select all overlay candidates that use Redis as a direct dependency',
    depRegex: /redis/i,
  },
];
// Maps each UI DB type to a regex that identifies the infra dep names it covers.
// Used to restrict auto-selection to only the specific DB type being branched,
// so enabling mysql branching doesn't also auto-select redis infra and vice versa.
const DB_TYPE_REGEX: Record<DbBranchUiType, RegExp> = {
  mysql:    /mysql/i,
  postgres: /postgres|^pg$/i,
  mongo:    /mongo/i,
  redis:    /redis/i,
};

// Temporary: all telapp apps should get Redis branching when the Redis flag is toggled,
// even before the appmetadata PR adding redis to their infra deps is merged.
// TODO: remove once Teladoc/telapp appmetadata redis deps PR is merged.
const TELAPP_REDIS_APPS = new Set<string>([
  'admin', 'callcenter', 'client', 'member', 'mobile-api', 'provider', 'tas',
]);

const EXTRA_REDIS_APPS = new Set<string>(['health-info', 'krono']);
const SYNTHETIC_REDIS_APPS = new Set<string>([...TELAPP_REDIS_APPS, ...EXTRA_REDIS_APPS]);

// ---------------------------------------------------------------------------
// OverlayCandidatesStep — main component
// ---------------------------------------------------------------------------
interface OverlayCandidatesStepProps {
  /** Step 1's session key (config.previewTag) — prefills the Kafka splitting message filter's
   *  Baggage regex, since that filter only ever matches this session's own traffic. */
  previewTag: string;
  candidates: OverlayCandidate[];
  baselineCandidates: BaselineCandidate[];
  loading: boolean;
  error: string | null;
  selectedServices: PreviewService[];
  onSelectionChange: (services: PreviewService[]) => void;
  infraServices: InfraService[];
  infraDepsMap: Record<string, string[]>;
  serviceDepsMap: Record<string, ServiceDependency[]>;
  selectedInfra: PreviewService[];
  onInfraChange: (infra: PreviewService[]) => void;
  dbBranch: string;
  onDbBranchChange: (branch: string) => void;
  serviceOverrides: Record<string, MirrordOverrides>;
  onOverridesChange: (overrides: Record<string, MirrordOverrides>) => void;
  suggestedServices?: string[];
  /** Reports whether any service has an automatic schema-migrations check in flight, so the
   *  wizard can block advancing past this step until every check resolves. */
  onPendingMigrationsChecksChange?: (pending: boolean) => void;
}

export const OverlayCandidatesStep: React.FC<OverlayCandidatesStepProps> = ({
  previewTag,
  candidates,
  baselineCandidates: _baselineCandidates,
  loading,
  error,
  selectedServices,
  onSelectionChange,
  infraServices,
  infraDepsMap,
  serviceDepsMap,
  selectedInfra,
  onInfraChange,
  serviceOverrides,
  onOverridesChange,
  suggestedServices = [],
  onPendingMigrationsChecksChange,
}) => {
  const [imageOverrides, setImageOverrides] = useState<Record<string, string>>({});
  const [expandedOverrides, setExpandedOverrides] = useState<Set<string>>(new Set());
  const [activeFeatureFlags, setActiveFeatureFlags] = useState<Set<string>>(new Set(['redis']));
  // Counts in-flight migrations-checks per service, not just a boolean — a service's branch can
  // change again before its previous check resolves (see the branch-aware effect below), so two
  // checks for the same service can genuinely overlap. Using add/delete on a plain Set would let
  // the earlier (now-stale) check's completion clear pending while the newer one is still in
  // flight, prematurely re-enabling deploy/advance.
  const [pendingMigrationsChecks, setPendingMigrationsChecks] = useState<Record<string, number>>({});

  const setMigrationsCheckPending = (appName: string, pending: boolean) => {
    setPendingMigrationsChecks(prev => {
      const count = (prev[appName] ?? 0) + (pending ? 1 : -1);
      if (count > 0) return { ...prev, [appName]: count };
      const { [appName]: _drop, ...rest } = prev;
      return rest;
    });
  };

  useEffect(() => {
    onPendingMigrationsChecksChange?.(Object.keys(pendingMigrationsChecks).length > 0);
  }, [pendingMigrationsChecks, onPendingMigrationsChecksChange]);

  const tokenGetter = useStableAccessToken();
  // Mirrors the latest serviceOverrides so the async migrations-catchup effect below can merge
  // its result against current state instead of the stale closure it was scheduled with —
  // several of these can be in flight concurrently (one per pre-existing db_branches entry), so
  // a plain closure over `serviceOverrides` would let one completion's onOverridesChange stomp
  // another's.
  const overridesRef = useRef(serviceOverrides);
  useEffect(() => { overridesRef.current = serviceOverrides; }, [serviceOverrides]);

  // Attaches/detaches automatic schema migrations whenever a service's branch changes, not just
  // on toggle-on — covers cache-restored DB branching and branch switches while it stays enabled.
  const lastCheckedBranchRef = useRef<Record<string, string>>({});
  useEffect(() => {
    const tasks: (() => Promise<void>)[] = [];
    for (const c of candidates) {
      if (!c.branch) continue;
      const branches = serviceOverrides[c.application]?.feature?.db_branches ?? [];
      if (branches.length === 0) {
        // Clear so a later re-enable on the same branch is treated as "never checked" and
        // re-fires — otherwise toggle off → toggle back on (same branch) sees a stale match here
        // and silently skips re-attaching migrations.
        delete lastCheckedBranchRef.current[c.application];
        continue;
      }
      // Skip (don't mark checked) until the image resolves, so this retries once it does.
      const serviceImage = imageOverrides[c.application] ?? '';
      if (!serviceImage) continue;
      if (lastCheckedBranchRef.current[c.application] === c.branch) continue;
      const requestedBranch = c.branch;
      lastCheckedBranchRef.current[c.application] = requestedBranch;

      tasks.push(async () => {
        setMigrationsCheckPending(c.application, true);
        try {
          const result = await apiMigrationsCheck(c.application, c.branch, tokenGetter);
          // Ignore this result if the service's branch changed again while the request was in
          // flight — lastCheckedBranchRef will already point at the newer branch by then.
          if (lastCheckedBranchRef.current[c.application] !== requestedBranch) return;
          const migrations = result.applicable && result.template
            ? { ...result.template, image: serviceImage }
            : undefined;
          const current = overridesRef.current[c.application] ?? {};
          const currentBranches = current.feature?.db_branches ?? [];
          const targetIndex = findSqlEntryIndex(currentBranches);
          if (targetIndex === -1) return;
          onOverridesChange({
            ...overridesRef.current,
            [c.application]: {
              ...current,
              feature: {
                ...current.feature,
                db_branches: currentBranches.map((b, i) => {
                  if (i !== targetIndex) return b;
                  if (migrations) return { ...b, migrations };
                  const { migrations: _drop, ...rest } = b;
                  return rest;
                }),
              },
            },
          });
        } catch {
          // fails safe — no migrations attached
        } finally {
          setMigrationsCheckPending(c.application, false);
        }
      });
    }
    // Caps fanout — see MIGRATIONS_CHECK_CONCURRENCY.
    void runWithConcurrencyLimit(tasks, MIGRATIONS_CHECK_CONCURRENCY);
  }, [candidates, serviceOverrides, imageOverrides, onOverridesChange, tokenGetter]);

  useEffect(() => {
    const defaults: Record<string, string> = {};
    candidates.forEach(c => {
      const tag = bestTag(c);
      const imageName = c.images?.image_name ?? c.application;
      defaults[c.application] = tag ? `${ARTIFACTORY_REGISTRY}/${imageName}:${tag}` : '';
    });
    setImageOverrides(defaults);
  }, [candidates]);

  // Repairs selections made before `imageOverrides` resolved for that service. toggleRow snapshots
  // imageOverrides[appName] at click time — if the row was selected while candidates/tags were
  // still loading (fetch in flight, or a later refetch produced a different image), the service
  // stays stuck in selectedServices with image: '' forever with nothing else to re-sync it, which
  // silently blocks the Next button with no visible cause. Backfill it as soon as a real image
  // becomes available, without touching services that already have one (never clobbers a
  // deliberate manual override).
  useEffect(() => {
    const fixes = selectedServices.filter(s => !s.image && imageOverrides[s.name]);
    if (fixes.length === 0) return;
    onSelectionChange(selectedServices.map(s =>
      !s.image && imageOverrides[s.name] ? { ...s, image: imageOverrides[s.name] } : s,
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageOverrides, selectedServices]);

  // Baseline candidates sharing a branched DB infra dep with a feature-branch app.
  // Currently disabled — only PR/feature-branch apps should appear as overlay candidates.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const activeBaselineCandidates = useMemo(() => [] as BaselineCandidate[], []);

  // Pre-fill image overrides for newly-visible baseline candidates.
  useEffect(() => {
    setImageOverrides(prev => {
      const updates: Record<string, string> = {};
      for (const bc of activeBaselineCandidates) {
        if (!prev[bc.application]) updates[bc.application] = bc.currentImage ?? '';
      }
      return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
    });
  }, [activeBaselineCandidates]);

  const imageValidationMap = useMemo(() => {
    const map: Record<string, 'found' | 'not found'> = {};
    candidates.forEach(c => {
      const foundTags = c.images?.found_tags ?? [];
      const currentTag = (imageOverrides[c.application] ?? '').split(':').pop() ?? '';
      map[c.application] = currentTag && foundTags.includes(currentTag) ? 'found' : 'not found';
    });
    return map;
  }, [candidates, imageOverrides]);

  const selectedNames = new Set(selectedServices.map(s => s.name));
  const featureCandidates = candidates.filter(hasAnyImage);
  const allCompanionParentByName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of candidates) {
      for (const companionName of c.companions ?? []) map[companionName] = c.application;
    }
    return map;
  }, [candidates]);
  // Kafka-consumer companion name -> parent name (e.g. 'tas-karafka' -> 'tas').
  const companionParentByName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of candidates) {
      for (const companionName of (c.companions ?? []).filter(name => KAFKA_DEP_REGEX.test(name))) {
        map[companionName] = c.application;
      }
    }
    return map;
  }, [candidates]);
  const candidatesByName = useMemo(() => new Map(candidates.map(c => [c.application, c])), [candidates]);
  // serviceDepsMap (from appmetadata's own `dependencies` list, e.g. tas requiring member-master)
  // is one-directional — only the parent declares the dep, not the other way around. Bundle-mate
  // auto-select should work either direction though: selecting tas should pull in member-master,
  // and selecting member-master should pull tas back in. Build the reverse lookup once and union
  // both directions per name below, rather than touching appmetadata to add a reciprocal entry
  // that wouldn't be semantically true (member-master doesn't actually depend on tas).
  const requiredDepNamesByParent = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const [parent, deps] of Object.entries(serviceDepsMap)) {
      const required = deps.filter(d => d.required).map(d => d.name);
      if (required.length > 0) map[parent] = required;
    }
    return map;
  }, [serviceDepsMap]);
  const parentNamesByRequiredDep = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const [parent, depNames] of Object.entries(requiredDepNamesByParent)) {
      for (const depName of depNames) (map[depName] ??= []).push(parent);
    }
    return map;
  }, [requiredDepNamesByParent]);
  const bundleMatesOf = (appName: string): string[] => [
    ...(requiredDepNamesByParent[appName] ?? []),
    ...(parentNamesByRequiredDep[appName] ?? []),
  ];
  const redisBranchingActive = activeFeatureFlags.has('redis')
    || Object.values(serviceOverrides).some(ov => (ov.feature?.db_branches ?? []).some(e => e.type === 'redis'));
  const isHiddenExtra = (c: OverlayCandidate) => EXTRA_REDIS_APPS.has(c.application) && !redisBranchingActive && !selectedNames.has(c.application);
  const visibleCandidates = candidates.filter(c => !isHiddenExtra(c));
  const selectableCandidates = [
    ...featureCandidates.filter(c => !isHiddenExtra(c)),
    ...activeBaselineCandidates.filter(bc => bc.isRunning !== false),
  ];
  const stoppedCandidates = candidates.filter(c => c.isRunning === false && !isHiddenExtra(c));
  const isAllSelected = selectableCandidates.length > 0 && selectableCandidates.every(c => selectedNames.has(c.application));

  // One-time-per-app guard (mirrors autoEnabledDbBranchRef below) — without it, this effect
  // re-fires on every selection change and re-adds any telapp/companion the user just manually
  // removed, making individual deselection impossible once any telapp app is selected.
  const autoAddedTelappRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const existingNames = new Set(selectedServices.map(s => s.name));
    if (![...TELAPP_REDIS_APPS].some(name => existingNames.has(name))) return;

    const toAdd: PreviewService[] = [];
    for (const c of featureCandidates) {
      if ((TELAPP_REDIS_APPS.has(c.application) || EXTRA_REDIS_APPS.has(c.application))
          && !existingNames.has(c.application) && !autoAddedTelappRef.current.has(c.application)) {
        toAdd.push({ name: c.application, image: imageOverrides[c.application] ?? '', type: (c.workloadType ?? 'deployment') as PreviewService['type'] });
        existingNames.add(c.application);
        autoAddedTelappRef.current.add(c.application);
      }
    }
    if (existingNames.has('tas')) {
      const tasCandidate = featureCandidates.find(c => c.application === 'tas');
      for (const companionName of tasCandidate?.companions ?? []) {
        if (existingNames.has(companionName) || autoAddedTelappRef.current.has(companionName)) continue;
        const companionCandidate = featureCandidates.find(c => c.application === companionName);
        if (companionCandidate) {
          toAdd.push({ name: companionName, image: imageOverrides[companionName] ?? '', type: (companionCandidate.workloadType ?? 'deployment') as PreviewService['type'] });
          existingNames.add(companionName);
          autoAddedTelappRef.current.add(companionName);
        }
      }
    }
    if (toAdd.length > 0) onSelectionChange([...selectedServices, ...toAdd]);
  }, [candidates, selectedServices]);

  const autoEnabledDbBranchRef = useRef<Set<string>>(new Set());
  // Persists the shared id minted for a given `${dbType}:${depName}` across effect runs — apps
  // sharing a Redis dep are usually selected one at a time (not in one batch), so the id a
  // sibling's branch was auto-created with must survive to be reused (and backfilled onto
  // earlier siblings) when a later sibling reveals the sharing relationship. See the retroactive
  // reconciliation below.
  const sharedIdByDepRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const nextOverrides: Record<string, MirrordOverrides> = { ...serviceOverrides };
    const updatedInfra = [...selectedInfra];
    const selectedInfraNames = new Set(updatedInfra.map(s => s.name));
    let changed = false;

    for (const dbType of ['mysql', 'postgres', 'mongo', 'redis'] as DbBranchUiType[]) {
      const depRegex = DB_TYPE_REGEX[dbType];
      const depNameOf = (c: OverlayCandidate) =>
        (infraDepsMap[c.application] ?? []).find(d => depRegex.test(d))
          ?? (dbType === 'redis' && SYNTHETIC_REDIS_APPS.has(c.application) ? 'redis' : undefined);

      // Every currently-selected, type-matching candidate — not just ones still needing a fresh
      // branch — so shared-id grouping reflects the true sibling set even when some of them were
      // already auto-enabled in an earlier, separate effect run (the normal case: a wizard user
      // checks services one at a time, not all simultaneously).
      const availableCandidates: OverlayCandidate[] = [];
      const toProcess: OverlayCandidate[] = [];
      for (const c of featureCandidates) {
        if (!selectedNames.has(c.application)) continue;
        const directInfraDeps = infraDepsMap[c.application] ?? [];
        const isAvailable = directInfraDeps.some(dep => depRegex.test(dep)) || (dbType === 'redis' && SYNTHETIC_REDIS_APPS.has(c.application));
        if (!isAvailable) continue;
        availableCandidates.push(c);

        const key = `${c.application}:${dbType}`;
        // Only marked done once a branch is actually created (see toProcess loop below) — marking
        // it here unconditionally meant a candidate whose envVarNames hadn't loaded yet on this
        // app's first-ever available render (matchEnvVarsToDbFields finding nothing, so no entry
        // got created) was permanently skipped on every later render too, even once real env var
        // data arrived — auto-enable silently never happening, requiring a manual toggle.
        if (autoEnabledDbBranchRef.current.has(key)) continue;
        const existing = nextOverrides[c.application] ?? {};
        if ((existing.feature?.db_branches ?? []).some(b => b.type === dbType)) {
          autoEnabledDbBranchRef.current.add(key);
          continue;
        }
        toProcess.push(c);
      }

      const sharedDepGroups = dbType === 'redis'
        ? groupBySharedDep(availableCandidates.map(c => ({ name: c.application, depNames: [depNameOf(c)].filter((d): d is string => !!d) })))
        : new Map<string, string[]>();
      // Reuse a depName's previously-minted id (ref persists across renders) rather than always
      // generating fresh — a stable id must survive across the separate effect runs that
      // discover each sibling, both for siblings created now and ones backfilled below.
      const sharedIdByDep = new Map(
        [...sharedDepGroups.keys()].map(depName => {
          const refKey = `${dbType}:${depName}`;
          let id = sharedIdByDepRef.current.get(refKey);
          if (!id) {
            id = `${depName}-shared-${crypto.randomUUID().slice(0, 8)}`;
            sharedIdByDepRef.current.set(refKey, id);
          }
          return [depName, id];
        }),
      );

      // Retroactive reconciliation: a sibling auto-enabled in an earlier run (before this
      // depName's sharing relationship was visible) still has its own solo id — bring it in line
      // now that the group is known. Redis-only, matching groupBySharedDep's redis-only gating
      // above (mysql/postgres/mongo pin a schema to whichever request first creates the branch,
      // so reusing an id across siblings with different schemas would silently give everyone the
      // first sibling's schema).
      if (dbType === 'redis') {
        for (const c of availableCandidates) {
          const depName = depNameOf(c);
          const sharedId = depName ? sharedIdByDep.get(depName) : undefined;
          if (!sharedId) continue;
          const existing = nextOverrides[c.application];
          const branches = existing?.feature?.db_branches;
          const idx = branches?.findIndex(b => b.type === dbType) ?? -1;
          if (idx === -1 || branches![idx].id === sharedId) continue;
          const nextBranches = branches!.map((b, i) => (i === idx ? { ...b, id: sharedId } : b));
          nextOverrides[c.application] = { ...existing, feature: { ...existing!.feature, db_branches: nextBranches } };
          changed = true;
        }
      }

      if (toProcess.length === 0) continue;

      for (const c of toProcess) {
        const existing = nextOverrides[c.application] ?? {};
        const existingBranches = existing.feature?.db_branches ?? [];

        const realDep = (infraDepsMap[c.application] ?? []).find(d => depRegex.test(d));
        const depName = realDep ?? (dbType === 'redis' && SYNTHETIC_REDIS_APPS.has(c.application) ? 'redis' : undefined);
        const params: Record<string, DbBranchParamValue> = matchEnvVarsToDbFields(c.envVarNames ?? [], dbType, depName);
        for (const [field, value] of Object.entries(c.dbBranchOverrides?.[dbType]?.env_var_overrides ?? {})) {
          if (value !== undefined) params[field] = value;
        }
        if (Object.keys(params).length === 0) continue;

        const versionDepName = realDep
          ?? (dbType === 'redis' && SYNTHETIC_REDIS_APPS.has(c.application) ? infraServices.find(i => i.name === 'redis')?.name : undefined);
        const versionImg = versionDepName ? infraServices.find(i => i.name === versionDepName)?.currentImage : undefined;
        const inferredVersion = versionImg ? inferVersionFromImage(versionImg, dbType) : undefined;

        const overrideProfile = c.dbBranchOverrides?.[dbType]?.profile;
        const overrideCopyMode = c.dbBranchOverrides?.[dbType]?.copy_mode;
        // A template's `image` wins over `version` entirely — mirrord treats the two as mutually
        // exclusive. Same resolution as ServiceOverridesPanel.tsx's toggleDbType — kept in sync
        // here since this auto-enable path now runs instead of that manual handler by default.
        // Uses the exact full tag (extractFullImageTag), not inferredVersion's truncated
        // major/major.minor form — that's correct for the bare `version` fallback below (the
        // operator maps it internally) but produces a non-existent Artifactory tag when spliced
        // straight into an `image` string (e.g. "9.6" from "9.6.3" 404s).
        const overrideImage = c.dbBranchOverrides?.[dbType]?.image;
        const fullTag = versionImg ? extractFullImageTag(versionImg) : undefined;
        const resolvedVersion = inferredVersion ?? DB_DEFAULT_VERSIONS[dbType];
        // Only the templated form needs fullTag; a literal image must not be discarded for lack of one.
        const resolvedImage = overrideImage
          ? (overrideImage.includes('$RANCH_INFERRED_VERSION')
              ? (fullTag ? overrideImage.replace('$RANCH_INFERRED_VERSION', fullTag) : undefined)
              : overrideImage)
          : undefined;
        const newBranch: DbBranchEntry = {
          id:      (depName && sharedIdByDep.get(depName)) || `${c.application}-${dbType}-${crypto.randomUUID().slice(0, 8)}`,
          type:    dbType,
          ...(resolvedImage ? { image: resolvedImage } : { version: resolvedVersion }),
          name: resolveDbBranchName(params, c.dbSchemaNames ?? {}, dbType, c.dbBranchOverrides?.[dbType]?.schema_name),
          connection: { params },
          copy: {
            mode: overrideCopyMode ?? (dbType === 'redis' ? 'empty' : 'all'),
            ...(c.dbBranchOverrides?.[dbType]?.dump_args ? { dump_args: c.dbBranchOverrides[dbType]!.dump_args } : {}),
          },
          ...(overrideProfile ? { profile: overrideProfile } : {}),
        };

        nextOverrides[c.application] = {
          ...existing,
          feature: { ...existing.feature, db_branches: [...existingBranches, newBranch] },
        };
        changed = true;
        autoEnabledDbBranchRef.current.add(`${c.application}:${dbType}`);

        for (const dep of infraDepsMap[c.application] ?? []) {
          if (depRegex.test(dep) && !selectedInfraNames.has(dep)) {
            const infraSvc = infraServices.find(i => i.name === dep);
            if (infraSvc) {
              updatedInfra.push({ name: infraSvc.name, image: infraSvc.currentImage ?? '', type: infraSvc.type });
              selectedInfraNames.add(dep);
            }
          }
        }
      }
    }

    if (changed) {
      onOverridesChange(nextOverrides);
      if (updatedInfra.length !== selectedInfra.length) onInfraChange(updatedInfra);
    }
    // serviceOverrides is a dependency so a manual toggle in ServiceOverridesPanel (which only
    // changes serviceOverrides, not candidates/selectedServices) still triggers the retroactive
    // shared-id reconciliation above — otherwise a manually-enabled redis branch keeps its solo id
    // forever. Safe against loops: once reconciled, `changed` stays false on the next run.
  }, [candidates, selectedServices, serviceOverrides]);

  // ---- Selection behavior ----

  const toggleAll = () => {
    if (isAllSelected) {
      onSelectionChange([]);
      // Same reasoning as toggleRow/toggleBaselineRow: nothing selected means nothing to deploy,
      // so nothing to branch out for any of them either. Cleared as one atomic update (not a loop
      // of individual handleOverrideChange calls, which would each read the same stale
      // serviceOverrides prop and stomp each other's result).
      const deselectedNames = new Set(selectedServices.map(s => s.name));
      const clearedOverrides = Object.fromEntries(
        Object.entries(serviceOverrides).filter(([name]) => !deselectedNames.has(name)),
      );
      if ([...deselectedNames].some(name => serviceOverrides[name])) {
        onOverridesChange(clearedOverrides);
      }
      // Only release infra that's no longer required by anything (mirrors handleOverrideChange's
      // depsToAutoUnselect diff) — never touch infra the user selected for unrelated reasons.
      const prevRequiredDeps = requiredDepsFromOverrides(serviceOverrides);
      const nextRequiredDeps = requiredDepsFromOverrides(clearedOverrides);
      const depsToAutoUnselect = [...prevRequiredDeps].filter(dep => !nextRequiredDeps.has(dep));
      if (depsToAutoUnselect.length > 0) {
        onInfraChange(selectedInfra.filter(s => !depsToAutoUnselect.includes(s.name)));
      }
    } else {
      const newServices: PreviewService[] = [
        ...featureCandidates
          .filter(c => c.isRunning !== false)
          .map(c => ({ name: c.application, image: imageOverrides[c.application] ?? '', type: (c.workloadType ?? 'deployment') as PreviewService['type'] })),
        ...activeBaselineCandidates
          .filter(bc => bc.isRunning !== false)
          .map(bc => ({ name: bc.application, image: imageOverrides[bc.application] ?? bc.currentImage ?? '', type: (bc.workloadType ?? 'deployment') as PreviewService['type'] })),
      ];
      onSelectionChange(newServices);
    }
  };

  const toggleRow = (candidate: OverlayCandidate) => {
    const appName = candidate.application;
    // A service can go from running to stopped after it was already selected (e.g. crashed
    // mid-session) — block selecting a stopped service, but always allow removing one that's
    // already selected, or it gets stuck checked-and-immovable with no way to exclude it from
    // the deploy payload (see the "1 service(s) stopped" banner above the table).
    if (candidate.isRunning === false && !selectedNames.has(appName)) return;
    if (selectedNames.has(appName)) {
      // Deselecting the parent also deselects its companions.
      const companionNames = new Set(candidate.companions ?? []);
      onSelectionChange(selectedServices.filter(s => s.name !== appName && !companionNames.has(s.name)));
      setExpandedOverrides(prev => {
        const next = new Set(prev);
        next.delete(appName);
        companionNames.forEach(name => next.delete(name));
        return next;
      });
      // Deselecting means this service won't deploy at all — nothing to branch out for it, so
      // clear its overrides too (also runs the existing infra/sibling auto-unselect symmetry via
      // handleOverrideChange, since an emptied `db_branches` looks like every type turning off).
      // Otherwise a stale override survives unselection and can block deploy validation for a
      // service that was never going to be included in the deploy payload anyway.
      if (serviceOverrides[appName]) handleOverrideChange(appName, {});
      companionNames.forEach(name => {
        if (serviceOverrides[name]) handleOverrideChange(name, {});
      });

      // Deselecting a "-karafka" companion makes its parent's Kafka splitting meaningless — the
      // consumer that would actually receive the split traffic is gone. Clear the parent's
      // split_queues too, so the two can't silently drift apart (mirrors the auto-select below,
      // which brings the companion along when splitting turns on).
      if (appName.endsWith('-karafka')) {
        const parentName = appName.slice(0, -'-karafka'.length);
        const parentOverrides = serviceOverrides[parentName];
        if (Object.keys(parentOverrides?.feature?.split_queues ?? {}).length > 0) {
          const { split_queues, ...restFeature } = parentOverrides!.feature!;
          handleOverrideChange(parentName, { ...parentOverrides, feature: restFeature });
        }
      }
    } else {
      const withApp: PreviewService[] = [...selectedServices, { name: appName, image: imageOverrides[appName] ?? '', type: (candidate.workloadType ?? 'deployment') as PreviewService['type'] }];
      // Hardcoded env var defaults (e.g. health-info's RAILS_ENV=staging) from the tmpl's
      // feature.default_env — pre-fill them the moment the service is selected, not just on
      // manual "+ add var". Checks feature.env.override specifically (not the whole overrides
      // object, which a prior deselect leaves as {} rather than removing, and which a service
      // with its own db_branches already has non-empty) — only env itself must be untouched, and
      // any existing feature (e.g. db_branches) is preserved, not replaced.
      const newOverrides: Record<string, MirrordOverrides> = {};
      const existingEnvOverride = serviceOverrides[appName]?.feature?.env?.override ?? {};
      if (candidate.defaultEnv && Object.keys(existingEnvOverride).length === 0) {
        newOverrides[appName] = {
          ...serviceOverrides[appName],
          feature: { ...serviceOverrides[appName]?.feature, env: { ...serviceOverrides[appName]?.feature?.env, override: { ...candidate.defaultEnv } } },
        };
      }
      // Companion deployments (tas-sidekiq, tas-karafka, ...) default-select alongside their
      // parent — same convenience the old always-force-included behavior gave, but the user can
      // still uncheck each one individually afterward via its own row.
      for (const companionName of candidate.companions ?? []) {
        if (selectedNames.has(companionName)) continue;
        // Use `candidates`, not `featureCandidates` — companions have no build of their own
        // and can fail the hasAnyImage filter.
        const companionCandidate = candidates.find(c => c.application === companionName);
        if (!companionCandidate) continue;
        withApp.push({
          name: companionName,
          image: imageOverrides[companionName] ?? '',
          type: (companionCandidate.workloadType ?? 'deployment') as PreviewService['type'],
        });
        const existingCompanionEnvOverride = serviceOverrides[companionName]?.feature?.env?.override ?? {};
        if (companionCandidate.defaultEnv && Object.keys(existingCompanionEnvOverride).length === 0) {
          newOverrides[companionName] = {
            ...serviceOverrides[companionName],
            feature: { ...serviceOverrides[companionName]?.feature, env: { ...serviceOverrides[companionName]?.feature?.env, override: { ...companionCandidate.defaultEnv } } },
          };
        }
      }
      // Bundle mates (e.g. member-master <-> tas) default-select alongside each other, same
      // convenience as companions above — but unlike companions, each keeps its own
      // mirrordOverrides/default_env (resolved from its own appmetadata release), since a bundle
      // mate is a fully independent app with its own repo/build, not a shared-image worker.
      for (const mateName of bundleMatesOf(appName)) {
        if (selectedNames.has(mateName)) continue;
        const mateCandidate = featureCandidates.find(c => c.application === mateName);
        if (!mateCandidate) continue;
        withApp.push({
          name: mateName,
          image: imageOverrides[mateName] ?? '',
          type: (mateCandidate.workloadType ?? 'deployment') as PreviewService['type'],
        });
        const existingMateEnvOverride = serviceOverrides[mateName]?.feature?.env?.override ?? {};
        if (mateCandidate.defaultEnv && Object.keys(existingMateEnvOverride).length === 0) {
          newOverrides[mateName] = {
            ...serviceOverrides[mateName],
            feature: { ...serviceOverrides[mateName]?.feature, env: { ...serviceOverrides[mateName]?.feature?.env, override: { ...mateCandidate.defaultEnv } } },
          };
        }
      }
      onSelectionChange(withApp);
      if (Object.keys(newOverrides).length > 0) onOverridesChange({ ...serviceOverrides, ...newOverrides });
    }
  };

  const toggleBaselineRow = (bc: BaselineCandidate) => {
    if (bc.isRunning === false) return;
    if (selectedNames.has(bc.application)) {
      onSelectionChange(selectedServices.filter(s => s.name !== bc.application));
      if (serviceOverrides[bc.application]) handleOverrideChange(bc.application, {});
    } else {
      const image = imageOverrides[bc.application] ?? bc.currentImage ?? '';
      onSelectionChange([...selectedServices, { name: bc.application, image, type: bc.workloadType ?? 'deployment' }]);
      const existingBaselineEnvOverride = serviceOverrides[bc.application]?.feature?.env?.override ?? {};
      if (bc.defaultEnv && Object.keys(existingBaselineEnvOverride).length === 0) {
        onOverridesChange({
          ...serviceOverrides,
          [bc.application]: {
            ...serviceOverrides[bc.application],
            feature: { ...serviceOverrides[bc.application]?.feature, env: { ...serviceOverrides[bc.application]?.feature?.env, override: { ...bc.defaultEnv } } },
          },
        });
      }
    }
  };

  const updateServiceImage = (appName: string, image: string) => {
    setImageOverrides(prev => ({ ...prev, [appName]: image }));
    onSelectionChange(selectedServices.map(s => s.name === appName ? { ...s, image } : s));
  };

  const toggleOverridePanel = (appName: string) =>
    setExpandedOverrides(prev => {
      const next = new Set(prev);
      next.has(appName) ? next.delete(appName) : next.add(appName);
      return next;
    });

  // Shared by handleOverrideChange (reconciling a live edit) and the mount-time sync effect below
  // (reconciling overrides restored from cache before any edit has happened).
  const requiredDepsFromOverrides = (overridesByService: Record<string, MirrordOverrides>): Set<string> => {
    const deps = new Set<string>();
    for (const [svcName, svcOverrides] of Object.entries(overridesByService)) {
      const dbBranches = svcOverrides.feature?.db_branches;
      const kafkaEnabled = Object.keys(svcOverrides.feature?.split_queues ?? {}).length > 0;
      if (!dbBranches?.length && !kafkaEnabled) continue;
      for (const dep of infraDepsMap[svcName] ?? []) {
        if (dbBranches?.length) {
          for (const entry of dbBranches) {
            const typeRegex = DB_TYPE_REGEX[entry.type];
            if (typeRegex?.test(dep)) deps.add(dep);
          }
        }
        if (kafkaEnabled && KAFKA_DEP_REGEX.test(dep)) deps.add(dep);
      }
    }
    return deps;
  };

  // Services can arrive with DB branching already enabled in `serviceOverrides` (restored from
  // localStorage cache) without ever going through handleOverrideChange — so the infra
  // auto-select reconciliation below never ran for them. This used to run once on mount ([] deps),
  // but that fires before the cache restore (a separate effect in the parent wizard, populated
  // async) and before `infraServices` itself finishes loading (also async) — so the one-shot run
  // frequently saw empty data and never caught up. Depending on the actual inputs instead lets it
  // re-run once they're populated; it's idempotent (only ever adds missing deps, never removes),
  // so re-running after onInfraChange settles is a safe no-op.
  useEffect(() => {
    const required = requiredDepsFromOverrides(serviceOverrides);
    if (required.size === 0) return;
    const selectedInfraNames = new Set(selectedInfra.map(s => s.name));
    const missing = [...required].filter(dep => !selectedInfraNames.has(dep));
    if (missing.length === 0) return;
    const additions = missing
      .map(dep => infraServices.find(i => i.name === dep))
      .filter((svc): svc is InfraService => !!svc)
      .map(svc => ({ name: svc.name, image: svc.currentImage ?? '', type: svc.type }));
    if (additions.length > 0) onInfraChange([...selectedInfra, ...additions]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceOverrides, infraServices]);

  // Services can arrive already selected (found/running, so pre-checked) without ever going
  // through toggleRow's select branch — so the template's feature.default_env prefill applied
  // there (lines 588-593 above) never runs for them. Same root cause and fix shape as the infra
  // reconciliation effect above: idempotent, only fills an empty env.override, safe to re-run.
  useEffect(() => {
    const newOverrides: Record<string, MirrordOverrides> = {};
    for (const name of selectedNames) {
      const candidate = featureCandidates.find(c => c.application === name);
      if (!candidate?.defaultEnv) continue;
      const existingEnvOverride = serviceOverrides[name]?.feature?.env?.override ?? {};
      if (Object.keys(existingEnvOverride).length > 0) continue;
      newOverrides[name] = {
        ...serviceOverrides[name],
        feature: { ...serviceOverrides[name]?.feature, env: { ...serviceOverrides[name]?.feature?.env, override: { ...candidate.defaultEnv } } },
      };
    }
    if (Object.keys(newOverrides).length > 0) onOverridesChange({ ...serviceOverrides, ...newOverrides });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServices, featureCandidates]);

  // Same "arrives already selected" gap as above, for bundle mates (bundleMatesOf, e.g.
  // member-master <-> tas): a pre-checked service never runs toggleRow's select branch, so its
  // bundle mate never gets auto-added. Idempotent — only adds missing mates, safe to re-run.
  useEffect(() => {
    const additions: PreviewService[] = [];
    const namesToAdd = new Set<string>();
    for (const name of selectedNames) {
      for (const mateName of bundleMatesOf(name)) {
        if (selectedNames.has(mateName) || namesToAdd.has(mateName)) continue;
        const mateCandidate = featureCandidates.find(c => c.application === mateName);
        if (!mateCandidate) continue;
        namesToAdd.add(mateName);
        additions.push({ name: mateName, image: imageOverrides[mateName] ?? '', type: (mateCandidate.workloadType ?? 'deployment') as PreviewService['type'] });
      }
    }
    if (additions.length > 0) onSelectionChange([...selectedServices, ...additions]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServices, featureCandidates, serviceDepsMap]);

  // Reverse preselection: when DB/Kafka option changes for service A,
  // keep selected services/infra in sync with direct deps required by enabled overrides.
  const handleOverrideChange = (appName: string, ov: MirrordOverrides) => {
    const finalOverrides: Record<string, MirrordOverrides> = { ...serviceOverrides, [appName]: ov };

    onOverridesChange(finalOverrides);

    // Kafka splitting only has an effect if the "-karafka" consumer companion is actually
    // deployed to receive the split traffic — auto-select it the same way toggleRow does when
    // the parent service itself gets checked. Only fires on the off→on transition; turning
    // splitting off does not force-deselect the companion (it may still be wanted in preview on
    // its own merits), but deselecting the companion does clear splitting (see toggleRow).
    const wasKafkaEnabled = Object.keys(serviceOverrides[appName]?.feature?.split_queues ?? {}).length > 0;
    const isKafkaEnabled = Object.keys(ov.feature?.split_queues ?? {}).length > 0;
    if (!wasKafkaEnabled && isKafkaEnabled) {
      const companionName = `${appName}-karafka`;
      if (!selectedNames.has(companionName)) {
        const companionCandidate = featureCandidates.find(c => c.application === companionName);
        if (companionCandidate) {
          onSelectionChange([...selectedServices, {
            name: companionName,
            image: imageOverrides[companionName] ?? '',
            type: (companionCandidate.workloadType ?? 'deployment') as PreviewService['type'],
          }]);
        }
      }
    }

    // telapp's mysql/redis apps must share one DB branch — auto-select all TELAPP_REDIS_APPS siblings.
    const wasDbBranchingEnabled = (serviceOverrides[appName]?.feature?.db_branches ?? []).some(e => e.type === 'mysql' || e.type === 'redis');
    const isDbBranchingEnabled = (ov.feature?.db_branches ?? []).some(e => e.type === 'mysql' || e.type === 'redis');
    const isRedisEnabled = (ov.feature?.db_branches ?? []).some(e => e.type === 'redis') && !(serviceOverrides[appName]?.feature?.db_branches ?? []).some(e => e.type === 'redis');
    if (TELAPP_REDIS_APPS.has(appName) && ((!wasDbBranchingEnabled && isDbBranchingEnabled) || isRedisEnabled)) {
      const existingNames = new Set(selectedServices.map(s => s.name));
      const toAdd: PreviewService[] = [];
      if (!wasDbBranchingEnabled && isDbBranchingEnabled && TELAPP_REDIS_APPS.has(appName)) {
        for (const c of featureCandidates) {
          if (TELAPP_REDIS_APPS.has(c.application) && !existingNames.has(c.application)) {
            toAdd.push({ name: c.application, image: imageOverrides[c.application] ?? '', type: (c.workloadType ?? 'deployment') as PreviewService['type'] });
            existingNames.add(c.application);
          }
        }
      }
      if (isRedisEnabled) {
        for (const c of featureCandidates) {
          if (EXTRA_REDIS_APPS.has(c.application) && !existingNames.has(c.application)) {
            toAdd.push({ name: c.application, image: imageOverrides[c.application] ?? '', type: (c.workloadType ?? 'deployment') as PreviewService['type'] });
            existingNames.add(c.application);
          }
        }
      }
      if (existingNames.has('tas')) {
        const tasCandidate = featureCandidates.find(c => c.application === 'tas');
        for (const companionName of tasCandidate?.companions ?? []) {
          if (existingNames.has(companionName)) continue;
          const companionCandidate = featureCandidates.find(c => c.application === companionName);
          if (companionCandidate) {
            toAdd.push({ name: companionName, image: imageOverrides[companionName] ?? '', type: (companionCandidate.workloadType ?? 'deployment') as PreviewService['type'] });
            existingNames.add(companionName);
          }
        }
      }
      if (toAdd.length > 0) onSelectionChange([...selectedServices, ...toAdd]);
    }

    const prevRequiredDeps = requiredDepsFromOverrides(serviceOverrides);
    const nextRequiredDeps = requiredDepsFromOverrides(finalOverrides);

    const depsToAutoSelect = new Set<string>();
    const depsToAutoUnselect = new Set<string>();
    for (const dep of nextRequiredDeps) {
      if (!prevRequiredDeps.has(dep)) depsToAutoSelect.add(dep);
    }
    for (const dep of prevRequiredDeps) {
      if (!nextRequiredDeps.has(dep)) depsToAutoUnselect.add(dep);
    }

    if (depsToAutoSelect.size === 0 && depsToAutoUnselect.size === 0) return;

    let updatedInfra = [...selectedInfra];

    if (depsToAutoSelect.size > 0) {
      const selectedInfraNames = new Set(updatedInfra.map(s => s.name));
      for (const dep of depsToAutoSelect) {
        const svc = infraServices.find(i => i.name === dep);
        if (svc && !selectedInfraNames.has(dep)) {
          updatedInfra.push({ name: svc.name, image: svc.currentImage ?? '', type: svc.type });
          selectedInfraNames.add(dep);
        }
      }
    }

    if (depsToAutoUnselect.size > 0) {
      updatedInfra = updatedInfra.filter(s => !depsToAutoUnselect.has(s.name) || nextRequiredDeps.has(s.name));
    }

    onInfraChange(updatedInfra);
  };

  const handleFeatureFlagToggle = (flagKey: string, depRegex: RegExp, enabled: boolean) => {
    setActiveFeatureFlags(prev => {
      const next = new Set(prev);
      enabled ? next.add(flagKey) : next.delete(flagKey);
      return next;
    });

    // flagKey matches a DbBranchUiType when the flag drives DB branching (e.g. 'redis')
    const dbType = (flagKey in DB_TYPE_REGEX) ? flagKey as DbBranchUiType : null;

    const flagCandidates = featureCandidates.filter(c =>
      (infraDepsMap[c.application] ?? []).some(dep => depRegex.test(dep)) ||
      (flagKey === 'redis' && SYNTHETIC_REDIS_APPS.has(c.application)),
    );

    if (enabled) {
      // 1. Auto-select services
      const existingNames = new Set(selectedServices.map(s => s.name));
      const toAdd: PreviewService[] = [];
      for (const c of flagCandidates) {
        if (existingNames.has(c.application)) continue;
        toAdd.push({ name: c.application, image: imageOverrides[c.application] ?? '', type: (c.workloadType ?? 'deployment') as PreviewService['type'] });
        existingNames.add(c.application);
      }
      if (flagKey === 'redis' && existingNames.has('tas')) {
        const tasCandidate = featureCandidates.find(c => c.application === 'tas');
        for (const companionName of tasCandidate?.companions ?? []) {
          if (existingNames.has(companionName)) continue;
          const companionCandidate = featureCandidates.find(c => c.application === companionName);
          if (companionCandidate) {
            toAdd.push({ name: companionName, image: imageOverrides[companionName] ?? '', type: (companionCandidate.workloadType ?? 'deployment') as PreviewService['type'] });
            existingNames.add(companionName);
          }
        }
      }
      if (toAdd.length > 0) onSelectionChange([...selectedServices, ...toAdd]);

      // 2. Auto-populate db_branches and sync infra for DB-typed flags
      if (dbType) {
        const nextOverrides: Record<string, MirrordOverrides> = { ...serviceOverrides };
        const updatedInfra = [...selectedInfra];
        const selectedInfraNames = new Set(updatedInfra.map(s => s.name));

        // Shared-branch id (PLATENG-1612): candidates sharing the same concrete depName (e.g.
        // several telapp apps sharing one Redis instance) get one id, so their branches connect
        // to the same real mirrord branch pod instead of independent copies — live-confirmed that
        // mirrord's branch identity is scoped by `id` alone, not by session/target. Fresh
        // randomUUID() per toggle action, never derived from anything stable, so re-toggling later
        // (or a concurrent tester of the same PR) never collides with a prior group.
        const depNameOf = (c: OverlayCandidate) =>
          (infraDepsMap[c.application] ?? []).find(d => depRegex.test(d))
            ?? (dbType === 'redis' && SYNTHETIC_REDIS_APPS.has(c.application) ? 'redis' : undefined);
        // Redis-only: id sharing only makes sense for one flat keyspace per instance (redis).
        // mysql/postgres/mongo pin a specific schema to whichever request first creates the
        // branch, so reusing an id across siblings with different schemas silently gives everyone
        // the first sibling's schema — same reasoning as the server-side `shouldShareId` gate in
        // previewenv.ts. If a future flag ever drives a non-redis type, this must stay gated or
        // it reproduces that exact bug.
        const sharedDepGroups = dbType === 'redis'
          ? groupBySharedDep(flagCandidates.map(c => ({ name: c.application, depNames: [depNameOf(c)].filter((d): d is string => !!d) })))
          : new Map<string, string[]>();
        const sharedIdByDep = new Map(
          [...sharedDepGroups.keys()].map(depName => [depName, `${depName}-shared-${crypto.randomUUID().slice(0, 8)}`]),
        );

        for (const c of flagCandidates) {
          const existing = nextOverrides[c.application] ?? {};
          const existingBranches = existing.feature?.db_branches ?? [];
          if (existingBranches.some(b => b.type === dbType)) continue;

          // Appmetadata overrides win when present for a field — same precedence as the
          // per-service toggle in ServiceOverridesPanel's toggleDbType.
          // TELAPP_REDIS_APPS candidates have no real infra dep to match yet (see its TODO above),
          // so `depName` would otherwise come back undefined — which makes matchEnvVarsToDbFields
          // fall back to an unaffiliated pattern match and blindly grab the first *_PASSWORD-shaped
          // var on the pod (e.g. MAIL_PASSWORD), even when the real password isn't set at all. A
          // non-empty placeholder disables that fallback; host/port still resolve fine since
          // REDIS_HOST/REDIS_PORT match the type keyword directly regardless of dep name.
          const realDep = (infraDepsMap[c.application] ?? []).find(d => depRegex.test(d));
          const depName = realDep
            ?? (dbType === 'redis' && SYNTHETIC_REDIS_APPS.has(c.application) ? 'redis' : undefined);
          const params: Record<string, DbBranchParamValue> = matchEnvVarsToDbFields(c.envVarNames ?? [], dbType, depName);
          for (const [field, value] of Object.entries(c.dbBranchOverrides?.[dbType]?.env_var_overrides ?? {})) {
            if (value !== undefined) params[field] = value;
          }
          if (Object.keys(params).length === 0) continue;

          // Exact name match, not depRegex — a loose /redis/i substring match can hit unrelated
          // leftover preview pods (e.g. "deployment-admin-preview-test-redis-...") whose name
          // just happens to contain "redis", grabbing their commit-tagged image instead.
          const versionDepName = realDep
            ?? (dbType === 'redis' && SYNTHETIC_REDIS_APPS.has(c.application) ? infraServices.find(i => i.name === 'redis')?.name : undefined);
          const versionImg = versionDepName ? infraServices.find(i => i.name === versionDepName)?.currentImage : undefined;
          const inferredVersion = versionImg ? inferVersionFromImage(versionImg, dbType) : undefined;

          const overrideProfile = c.dbBranchOverrides?.[dbType]?.profile;
          const overrideCopyMode = c.dbBranchOverrides?.[dbType]?.copy_mode;
          // A template's `image` wins over `version` entirely — mirrord treats the two as mutually
          // exclusive. Same resolution as ServiceOverridesPanel.tsx's toggleDbType — kept in sync
          // here since this auto-enable path now runs instead of that manual handler by default.
          // Uses the exact full tag, not inferredVersion's truncated major/major.minor form — that
          // truncated form is correct for the bare `version` fallback below (the operator maps it
          // internally) but produces a non-existent Artifactory tag when spliced into an `image`
          // string (e.g. "9.6" from "9.6.3" 404s).
          const overrideImage = c.dbBranchOverrides?.[dbType]?.image;
          const fullTag = versionImg ? extractFullImageTag(versionImg) : undefined;
          const resolvedVersion = inferredVersion ?? DB_DEFAULT_VERSIONS[dbType];
          const resolvedImage = overrideImage
            ? (fullTag ? overrideImage.replace('$RANCH_INFERRED_VERSION', fullTag) : undefined)
            : undefined;
          const newBranch: DbBranchEntry = {
            id:      (depName && sharedIdByDep.get(depName)) || `${c.application}-${dbType}-${crypto.randomUUID().slice(0, 8)}`,
            type:    dbType,
            ...(resolvedImage ? { image: resolvedImage } : { version: resolvedVersion }),
            connection: { params },
            // "all" needs a real data-copy connection to the branch pod, which for TLS-native
            // redis hits an unresolved mirrord-operator bug (see ServiceOverridesPanel.tsx's
            // toggleDbType for the full explanation) — default redis to "empty" instead, unless
            // the app's own mirrord-preview.tmpl explicitly opts into "all" (e.g. plain redis-ccm,
            // which doesn't hit that bug). This loop is redis-only today (see the sharedDepGroups
            // gate above), but keep the check explicit so a future non-redis dbType here doesn't
            // inherit the wrong default.
            copy: { mode: overrideCopyMode ?? (dbType === 'redis' ? 'empty' : 'all') },
            ...(overrideProfile ? { profile: overrideProfile } : {}),
          };

          nextOverrides[c.application] = {
            ...existing,
            feature: {
              ...existing.feature,
              db_branches: [...existingBranches, newBranch],
            },
          };

          // Auto-select the matching infra service (e.g. redis pod)
          for (const dep of infraDepsMap[c.application] ?? []) {
            if (DB_TYPE_REGEX[dbType].test(dep) && !selectedInfraNames.has(dep)) {
              const infraSvc = infraServices.find(i => i.name === dep);
              if (infraSvc) {
                updatedInfra.push({ name: infraSvc.name, image: infraSvc.currentImage ?? '', type: infraSvc.type });
                selectedInfraNames.add(dep);
              }
            }
          }
        }

        onOverridesChange(nextOverrides);
        if (updatedInfra.length !== selectedInfra.length) onInfraChange(updatedInfra);
      }
    } else {
      // 1. Deselect services
      const flagNames = new Set(flagCandidates.map(c => c.application));
      onSelectionChange(selectedServices.filter(s => !flagNames.has(s.name)));

      // 2. Clear db_branches for this type and sync infra
      if (dbType) {
        const nextOverrides: Record<string, MirrordOverrides> = { ...serviceOverrides };
        const infraToRelease = new Set<string>();

        for (const c of flagCandidates) {
          if (!nextOverrides[c.application]) continue;
          const existing = nextOverrides[c.application];
          const remainingBranches = (existing.feature?.db_branches ?? []).filter(b => b.type !== dbType);
          nextOverrides[c.application] = {
            ...existing,
            feature: { ...existing.feature, db_branches: remainingBranches },
          };
          for (const dep of infraDepsMap[c.application] ?? []) {
            if (DB_TYPE_REGEX[dbType].test(dep)) infraToRelease.add(dep);
          }
        }

        // Keep infra that's still referenced by other services' db_branches
        const stillNeeded = new Set<string>();
        for (const [svcName, ov] of Object.entries(nextOverrides)) {
          for (const b of ov.feature?.db_branches ?? []) {
            for (const dep of infraDepsMap[svcName] ?? []) {
              if (DB_TYPE_REGEX[b.type]?.test(dep)) stillNeeded.add(dep);
            }
          }
        }

        onOverridesChange(nextOverrides);
        const updatedInfra = selectedInfra.filter(s => !infraToRelease.has(s.name) || stillNeeded.has(s.name));
        if (updatedInfra.length !== selectedInfra.length) onInfraChange(updatedInfra);
      }
    }
  };

  // ---- Loading / error / empty ----
  if (loading) return (
    <Bullseye>
      <Spinner size="lg" />
      <span style={{ marginLeft: 12 }}>Checking branch builds in namespace...</span>
    </Bullseye>
  );

  if (error) {
    const isAuthError = /401|unauthorized/i.test(error);
    return (
      <Alert
        variant="danger"
        title={isAuthError ? 'Session expired' : 'Failed to load overlay candidates'}
        isInline
      >
        {isAuthError
          ? 'Your session has expired after a period of inactivity. Please reload the page to continue.'
          : error}
      </Alert>
    );
  }

  if (candidates.length === 0) return (
    <EmptyState>
      <EmptyStateBody>
        No services found that both have a build on the selected branch and are active in the baseline namespace.
      </EmptyStateBody>
    </EmptyState>
  );

  return (
    <div>
      {stoppedCandidates.length > 0 && (
        <Alert
          variant="warning"
          isInline
          title={`${stoppedCandidates.length} service(s) stopped - cannot be selected for preview`}
          style={{ marginBottom: 12 }}
        >
          mirrord requires the baseline workload to be running. Start the following before deploying:{' '}
          {stoppedCandidates.map(c => c.application).join(', ')}
        </Alert>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>
          Services ({selectableCandidates.length})
          {selectedNames.size > 0 && <span style={{ ...changedBadge, background: C.purpleBg, color: C.purple }}>{selectedNames.size} selected</span>}
        </span>
        <Button variant="link" onClick={toggleAll}>{isAllSelected ? 'Unselect All' : 'Select All'}</Button>
      </div>

      <Table aria-label="Overlay candidates" variant="compact">
        <Thead>
          <Tr>
            <Th />
            <Th>Service</Th>
            <Th>Branch</Th>
            <Th>Status</Th>
            <Th>Image Status</Th>
            <Th>Latest Commits</Th>
            <Th>Image</Th>
            <Th>Advanced options</Th>
          </Tr>
        </Thead>
        <Tbody>
          {visibleCandidates.map(c => {
            const isSelected = selectedNames.has(c.application);
            const stopped = c.isRunning === false;
            // No known build doesn't block selection — the Image cell below still lets the user
            // pick another available tag (e.g. an older commit) to deploy with, the same way
            // manage-deploy allows overriding to a different build.
            const noKnownBuild = !hasAnyImage(c);
            // Only blocks *selecting* a stopped candidate — a stopped one that's already selected
            // (crashed after selection) must stay clickable so it can be removed. See toggleRow.
            const disabled = stopped && !isSelected;
            const imageName = c.images?.image_name ?? c.application;
            const deployTags = c.images?.deploy_tags ?? [];
            const versions = c.images?.versions ?? {};
            const validation = imageValidationMap[c.application];
            const overrideCount = countOverrides(serviceOverrides[c.application]).total;
            const panelOpen = expandedOverrides.has(c.application);
            const directInfraDeps = infraDepsMap[c.application] ?? [];
            const availableDbTypes = (['mysql', 'postgres', 'mongo', 'redis'] as DbBranchUiType[])
              .filter(type => directInfraDeps.some(dep => DB_TYPE_REGEX[type].test(dep)));
            const kafkaAvailable = directInfraDeps.some(dep => KAFKA_DEP_REGEX.test(dep));
            const kafkaInheritedFrom = companionParentByName[c.application];
            const kafkaAppliesTo = (c.companions ?? []).filter(name => KAFKA_DEP_REGEX.test(name));
            // The toggle usually lives on the parent (e.g. "tas"), which has no catalog of its
            // own — fall back to a kafka-type companion's catalog (e.g. "tas-karafka"'s).
            const kafkaTopics = c.kafkaTopics
              ?? kafkaAppliesTo.map(name => candidatesByName.get(name)?.kafkaTopics).find((t): t is NonNullable<typeof t> => !!t?.length);
            const dbInheritedFrom = allCompanionParentByName[c.application];
            const dbAppliesTo = c.companions ?? [];
            const dbTypeVersions = Object.fromEntries(
              availableDbTypes.flatMap(type => {
                const dep = directInfraDeps.find(d => DB_TYPE_REGEX[type].test(d));
                const img = dep ? infraServices.find(i => i.name === dep)?.currentImage : undefined;
                const v = img ? inferVersionFromImage(img, type) : undefined;
                return v ? [[type, v]] : [];
              }),
            ) as Partial<Record<DbBranchUiType, string>>;
            // Exact full tags, for resolving a template's `image` field's $RANCH_INFERRED_VERSION —
            // dbTypeVersions above is truncated to major/major.minor, which 404s when spliced
            // straight into a real Artifactory tag (e.g. "9.6" from "9.6.3").
            const dbTypeFullTags = Object.fromEntries(
              availableDbTypes.flatMap(type => {
                const dep = directInfraDeps.find(d => DB_TYPE_REGEX[type].test(d));
                const img = dep ? infraServices.find(i => i.name === dep)?.currentImage : undefined;
                const v = img ? extractFullImageTag(img) : undefined;
                return v ? [[type, v]] : [];
              }),
            ) as Partial<Record<DbBranchUiType, string>>;
            const dbTypeDepNames = Object.fromEntries(
              availableDbTypes.flatMap(type => {
                const dep = directInfraDeps.find(d => DB_TYPE_REGEX[type].test(d));
                return dep ? [[type, dep]] : [];
              }),
            ) as Partial<Record<DbBranchUiType, string>>;

            const allOptions = deployTags.map(tag => {
              const ts = versions[tag];
              return ts ? `${tag} (${ts})` : tag;
            });

            return (
              <React.Fragment key={c.application}>
                <Tr selected={isSelected}>
                  <Td select={{ rowIndex: candidates.indexOf(c), onSelect: () => toggleRow(c), isSelected, isDisabled: disabled }} />
                  <Td>
                    {c.application}
                    {suggestedServices.includes(c.application) && (
                      <span style={{ fontSize: 11, color: '#0052CC', marginLeft: 6, fontWeight: 500 }}>from PR</span>
                    )}
                  </Td>
                  <Td>{c.branch}</Td>
                  <Td>{stopped ? <Label color="orange">stopped</Label> : <Label color="green">running</Label>}</Td>
                  <Td>
                    {noKnownBuild && !stopped ? <Label color="red">No images in Artifactory</Label>
                      : !stopped && (validation === 'found' ? <Label color="green">found</Label> : <Label color="red">not found</Label>)}
                  </Td>
                  <Td>
                    {allOptions.length > 0 ? (
                      <EditableImageCell
                        imageValue={(() => {
                          const t = (imageOverrides[c.application] ?? '').split(':').pop() ?? '';
                          return allOptions.find(o => stripAnnotation(o) === t) ?? allOptions[0];
                        })()}
                        dummyImages={allOptions}
                        onChange={value => {
                          const tag = stripAnnotation(value);
                          updateServiceImage(c.application, `${ARTIFACTORY_REGISTRY}/${imageName}:${tag}`);
                        }}
                      />
                    ) : <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>-</span>}
                  </Td>
                  <Td>
                    {isSelected && (
                      <TextInput
                        aria-label={`Image for ${c.application}`}
                        value={(imageOverrides[c.application] ?? '').split(':').pop() ?? ''}
                        onChange={(_e, v) => {
                          const full = v.includes('/') ? v : `${ARTIFACTORY_REGISTRY}/${imageName}:${v}`;
                          updateServiceImage(c.application, full);
                        }}
                        placeholder="commit-abc1234"
                        style={{ minWidth: 180 }}
                      />
                    )}
                  </Td>
                  <Td>
                    {isSelected && (
                      <button
                        onClick={() => toggleOverridePanel(c.application)}
                        style={{
                          background: panelOpen ? C.purpleBg : 'transparent',
                          border: `1px solid ${panelOpen ? C.purple : C.border}`,
                          borderRadius: 4,
                          padding: '3px 10px',
                          fontSize: 12,
                          cursor: 'pointer',
                          color: panelOpen ? C.purple : C.muted,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {panelOpen ? '▲' : '▼'} Advanced options
                        {overrideCount > 0 && <span style={changedBadge}>{overrideCount} changed</span>}
                      </button>
                    )}
                  </Td>
                </Tr>
                {isSelected && panelOpen && (
                  <Tr>
                    <Td colSpan={8} style={{ padding: 0, border: 'none' }}>
                      <ServiceOverridesPanel
                        previewTag={previewTag}
                        serviceName={c.application}
                        overrides={serviceOverrides[c.application] ?? {}}
                        availableDbTypes={availableDbTypes}
                        kafkaAvailable={kafkaAvailable}
                        kafkaTopics={kafkaTopics}
                        kafkaInheritedFrom={kafkaInheritedFrom}
                        kafkaAppliesTo={kafkaAppliesTo}
                        dbInheritedFrom={dbInheritedFrom}
                        dbAppliesTo={dbAppliesTo}
                        onChange={ov => handleOverrideChange(c.application, ov)}
                        envVarNames={c.envVarNames}
                        dbSchemaNames={c.dbSchemaNames}
                        dbBranchOverrides={c.dbBranchOverrides}
                        dbTypeVersions={dbTypeVersions}
                        dbTypeFullTags={dbTypeFullTags}
                        dbTypeDepNames={dbTypeDepNames}
                        migrationsCheckPending={(pendingMigrationsChecks[c.application] ?? 0) > 0}
                      />
                    </Td>
                  </Tr>
                )}
              </React.Fragment>
            );
          })}
          {activeBaselineCandidates.map((bc, idx) => {
            const isSelected = selectedNames.has(bc.application);
            const stopped = bc.isRunning === false;
            const currentImage = imageOverrides[bc.application] ?? bc.currentImage ?? '';
            const directInfraDeps = infraDepsMap[bc.application] ?? [];
            const availableDbTypes = (['mysql', 'postgres', 'mongo', 'redis'] as DbBranchUiType[])
              .filter(type => directInfraDeps.some(dep => DB_TYPE_REGEX[type].test(dep)));
            const kafkaAvailable = directInfraDeps.some(dep => KAFKA_DEP_REGEX.test(dep));
            const dbTypeVersions = Object.fromEntries(
              availableDbTypes.flatMap(type => {
                const dep = directInfraDeps.find(d => DB_TYPE_REGEX[type].test(d));
                const img = dep ? infraServices.find(i => i.name === dep)?.currentImage : undefined;
                const v = img ? inferVersionFromImage(img, type) : undefined;
                return v ? [[type, v]] : [];
              }),
            ) as Partial<Record<DbBranchUiType, string>>;
            // Exact full tags, for resolving a template's `image` field's $RANCH_INFERRED_VERSION —
            // dbTypeVersions above is truncated to major/major.minor, which 404s when spliced
            // straight into a real Artifactory tag (e.g. "9.6" from "9.6.3").
            const dbTypeFullTags = Object.fromEntries(
              availableDbTypes.flatMap(type => {
                const dep = directInfraDeps.find(d => DB_TYPE_REGEX[type].test(d));
                const img = dep ? infraServices.find(i => i.name === dep)?.currentImage : undefined;
                const v = img ? extractFullImageTag(img) : undefined;
                return v ? [[type, v]] : [];
              }),
            ) as Partial<Record<DbBranchUiType, string>>;
            const dbTypeDepNames = Object.fromEntries(
              availableDbTypes.flatMap(type => {
                const dep = directInfraDeps.find(d => DB_TYPE_REGEX[type].test(d));
                return dep ? [[type, dep]] : [];
              }),
            ) as Partial<Record<DbBranchUiType, string>>;
            const overrideCount = countOverrides(serviceOverrides[bc.application]).total;
            const panelOpen = expandedOverrides.has(bc.application);
            const fallbackBadge: React.CSSProperties = { ...changedBadge, background: '#F4F5F7', color: C.muted, border: `1px solid ${C.border}` };

            return (
              <React.Fragment key={bc.application}>
                <Tr selected={isSelected}>
                  <Td select={{ rowIndex: candidates.length + idx, onSelect: () => toggleBaselineRow(bc), isSelected, isDisabled: stopped }} />
                  <Td>
                    {bc.application}
                    <span style={fallbackBadge}>fallback</span>
                  </Td>
                  <Td><span style={{ color: C.muted, fontSize: 12 }}>master</span></Td>
                  <Td>{stopped ? <Label color="orange">stopped</Label> : <Label color="green">running</Label>}</Td>
                  <Td><Label color="blue">baseline image</Label></Td>
                  <Td><span style={{ fontStyle: 'italic', color: C.muted, fontSize: 12 }}>-</span></Td>
                  <Td>
                    {isSelected && (
                      <TextInput
                        aria-label={`Image for ${bc.application}`}
                        value={currentImage.split(':').pop() ?? ''}
                        onChange={(_e, v) => {
                          const full = v.includes('/') ? v : `${ARTIFACTORY_REGISTRY}/${bc.application}:${v}`;
                          updateServiceImage(bc.application, full);
                        }}
                        placeholder="current baseline image"
                        style={{ minWidth: 180 }}
                      />
                    )}
                  </Td>
                  <Td>
                    {isSelected && (
                      <button
                        onClick={() => toggleOverridePanel(bc.application)}
                        style={{
                          background: panelOpen ? C.purpleBg : 'transparent',
                          border: `1px solid ${panelOpen ? C.purple : C.border}`,
                          borderRadius: 4,
                          padding: '3px 10px',
                          fontSize: 12,
                          cursor: 'pointer',
                          color: panelOpen ? C.purple : C.muted,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {panelOpen ? '▲' : '▼'} Advanced options
                        {overrideCount > 0 && <span style={changedBadge}>{overrideCount} changed</span>}
                      </button>
                    )}
                  </Td>
                </Tr>
                {isSelected && panelOpen && (
                  <Tr>
                    <Td colSpan={8} style={{ padding: 0, border: 'none' }}>
                      <ServiceOverridesPanel
                        previewTag={previewTag}
                        serviceName={bc.application}
                        overrides={serviceOverrides[bc.application] ?? {}}
                        availableDbTypes={availableDbTypes}
                        kafkaAvailable={kafkaAvailable}
                        kafkaTopics={bc.kafkaTopics}
                        onChange={ov => handleOverrideChange(bc.application, ov)}
                        envVarNames={bc.envVarNames}
                        dbSchemaNames={bc.dbSchemaNames}
                        dbBranchOverrides={bc.dbBranchOverrides}
                        dbTypeVersions={dbTypeVersions}
                        dbTypeFullTags={dbTypeFullTags}
                        dbTypeDepNames={dbTypeDepNames}
                      />
                    </Td>
                  </Tr>
                )}
              </React.Fragment>
            );
          })}
        </Tbody>
      </Table>

      {FEATURE_FLAGS.length > 0 && (
        <div style={{ marginTop: 20, marginBottom: 4 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Feature Flags</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {FEATURE_FLAGS.map(flag => (
              <div key={flag.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Switch
                  id={`feature-flag-${flag.key}`}
                  label={flag.label}
                  labelOff={flag.label}
                  isChecked={activeFeatureFlags.has(flag.key)}
                  onChange={(_e, checked) => handleFeatureFlagToggle(flag.key, flag.depRegex, checked)}
                />
                <span style={{ fontSize: 12, color: C.muted }}>{flag.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <InfraSection
        infraServices={infraServices}
        selectedInfra={selectedInfra}
        onInfraChange={onInfraChange}
      />
    </div>
  );
};
